import React from "react";
import { Box, Chip, Stack } from "@mui/material";
import dayjs from "dayjs";

export const TIME_TOKEN_RE = /\b\d{2}:\d{2}\b/g;
export const DIVIDER = "DIVIDER_LINE";
export const DOW_TO_RULE = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKEND_DAYS = new Set([0, 6]);

export const isDashOnly = (v) => {
  const s = String(v ?? "").trim();
  return s === "-" || s === "—";
};

export const extractTimes = (val) => {
  const s = String(val ?? "");
  const m = s.match(TIME_TOKEN_RE);
  return m ? m.slice() : [];
};

export const normalizeBaseUrl = (raw) => {
  if (!raw) return "";
  const trimmed = raw.trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const isWeekendIso = (iso) => WEEKEND_DAYS.has(dayjs(iso).day());
export const getDayKeyFromIso = (iso) => DOW_TO_RULE[dayjs(iso).day()] || "";

export const hhmmToMinutes = (t) => {
  const s = String(t || "").trim();
  if (!/^\d{2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

export const minutesDiff = (tIn, tOut) => {
  if (!tIn || !tOut) return 0;
  const dIn = dayjs(`2020-01-01T${tIn}:00`);
  const dOut = dayjs(`2020-01-01T${tOut}:00`);
  const diff = dOut.diff(dIn, "minute");
  return diff > 0 && diff < 24 * 60 ? diff : 0;
};

export const isTimeCellComplete = (val) => {
  const s = String(val ?? "").trim();
  if (!s || isDashOnly(s)) return true;
  const matches = s.match(TIME_TOKEN_RE);
  if (!matches || matches.length === 0) return true;
  return matches.length % 2 === 0;
};

export const makeDirtyKey = (empId, iso) => `${String(empId)}|${iso}`;

// export const encodeSegmentsToCellText = (segments, leaveOptions) => {
//   const nameById = new Map(
//     (leaveOptions || []).map((x) => [String(x.leaveid), String(x.leavename || "")]),
//   );

//   const lines = [];
//   (segments || []).forEach((seg) => {
//     const tIn = String(seg?.time_in ?? "").trim();
//     const tOut = String(seg?.time_out ?? "").trim();
//     if (!tIn && !tOut) return; // ← skips if BOTH empty

//     const times = [tIn, tOut].filter(Boolean).join(" ~ ");
//     const mode = String(seg?.mode || "WORK").toUpperCase();
//     const leaveid = String(seg?.leaveid ?? "DAWAM");

//     // mhmd dakdouke
//     if (mode === "WORK" || leaveid === "DAWAM") {
//       lines.push(`working: ${times}`);
//     } else {
//       const name = nameById.get(leaveid) || `Leave ${leaveid}`;
//       lines.push(`${name}: ${times}`);
//     }
    
//   });
export const encodeSegmentsToCellText = (segments, leaveOptions) => {
  const nameById = new Map(
    (leaveOptions || []).map((x) => [String(x.leaveid), String(x.leavename || "")]),
  );

  const toMinutes = (hhmm) => {
    const s = String(hhmm ?? "").trim();
    if (!s) return Infinity; // segments with no time_in sort last
    const [h, m] = s.split(":").map((n) => parseInt(n, 10));
    if (Number.isNaN(h)) return Infinity;
    return h * 60 + (Number.isNaN(m) ? 0 : m);
  };

  const sortedSegments = [...(segments || [])].sort(
    (a, b) => toMinutes(a?.time_in) - toMinutes(b?.time_in),
  );

  const lines = [];
  sortedSegments.forEach((seg) => {
    const tIn = String(seg?.time_in ?? "").trim();
    const tOut = String(seg?.time_out ?? "").trim();
    if (!tIn && !tOut) return;

    const times = [tIn, tOut].filter(Boolean).join(" ~ ");
    const mode = String(seg?.mode || "WORK").toUpperCase();
    const leaveid = String(seg?.leaveid ?? "DAWAM");

    if (mode === "WORK" || leaveid === "DAWAM") {
      lines.push(`working: ${times}`);
    } else {
      const name = nameById.get(leaveid) || `Leave ${leaveid}`;
      lines.push(`${name}: ${times}`);
    }
  });

  if (!lines.length) return "";

  const out = [];
  lines.forEach((l, i) => {
    if (i > 0) out.push(DIVIDER);
    out.push(l);
  });

  return out.join("\n");
};



export const decodeCellTextToSegments = (cellText, leaveOptions) => {
  const nameToId = new Map(
    (leaveOptions || []).map((x) => [
      String(x.leavename || "").trim().toLowerCase(),
      String(x.leaveid),
    ]),
  );

  const lines = String(cellText ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && l !== DIVIDER);

  const segments = [];

  lines.forEach((line) => {
    const parts = line.split(":");
    if (parts.length < 2) return;

    const head = parts[0].trim();
    const headLower = head.toLowerCase();
    const rest = parts.slice(1).join(":").trim();
    const times = extractTimes(rest);

    const time_in = times[0] || "";
    const time_out = times[1] || "";
    if (!time_in && !time_out) return;

    const isWorking =
      headLower === "working" || headLower === "dawam" || head === "دوام";

    if (isWorking) {
      segments.push({ mode: "WORK", leaveid: "DAWAM", time_in, time_out });
      return;
    }

    const leaveid = nameToId.get(headLower) || "";
    segments.push({ mode: "LEAVE", leaveid, time_in, time_out });
  });

  if (!segments.length) {
    const raw = extractTimes(cellText);
    if (raw.length >= 2) {
      segments.push({
        mode: "WORK",
        leaveid: "DAWAM",
        time_in: raw[0],
        time_out: raw[1],
      });
    }
  }

  return segments;
};

export const getWorkingSegmentsFromCellText = (cellText, leaveOptions) => {
  const segments = decodeCellTextToSegments(cellText, leaveOptions);
  return segments.filter((seg) => {
    const mode = String(seg?.mode || "").toUpperCase();
    const leaveid = String(seg?.leaveid || "").toUpperCase();
    return mode === "WORK" || leaveid === "DAWAM";
  });
};

export const getWorkingMinMaxFromCellText = (cellText, leaveOptions) => {
  const workSegs = getWorkingSegmentsFromCellText(cellText, leaveOptions);
  const mins = [];
  const maxs = [];

  workSegs.forEach((seg) => {
    const tIn = hhmmToMinutes(seg?.time_in);
    const tOut = hhmmToMinutes(seg?.time_out);
    if (tIn != null) mins.push(tIn);
    if (tOut != null) maxs.push(tOut);
  });

  return {
    minIn: mins.length ? Math.min(...mins) : null,
    maxOut: maxs.length ? Math.max(...maxs) : null,
    hasWork: workSegs.length > 0,
  };
};

export const getSupposedMinMaxFromRuleText = (ruleTxt) => {
  const times = extractTimes(ruleTxt);
  if (!times.length) return { minIn: null, maxOut: null };

  const mins = [];
  const maxs = [];

  for (let i = 0; i < times.length; i += 2) {
    const tIn = hhmmToMinutes(times[i]);
    const tOut = hhmmToMinutes(times[i + 1]);

    if (tIn != null) mins.push(tIn);
    if (tOut != null) maxs.push(tOut);
  }

  return {
    minIn: mins.length ? Math.min(...mins) : null,
    maxOut: maxs.length ? Math.max(...maxs) : null,
  };
};

export const RULE_METHOD_KEYS = {
  EC: "EC",
  EL: "EL",
  LC: "LC",
  LL: "LL",
};

export const findFactorForMinutes = (ruleRows, methodKey, minutes) => {
  const list = (Array.isArray(ruleRows) ? ruleRows : [])
    .filter(
      (r) =>
        String(r?.PoliciesMethodType || "").toUpperCase().trim() ===
        String(methodKey || "").toUpperCase().trim(),
    )
    .sort((a, b) => Number(a?.ToMinute ?? 0) - Number(b?.ToMinute ?? 0));

  const value = Math.abs(Number(minutes || 0));
  if (!value || !list.length) return { usedMinutes: 0, factor: 0 };

  const matched = list.find((r) => {
    const from = Number(r?.FromMinute ?? 0);
    const to = Number(r?.ToMinute ?? 0);
    return value >= from && value <= to;
  });

  if (matched) {
    return {
      usedMinutes: value,
      factor: Number(matched?.FactorNb ?? 0),
    };
  }

  const last = list[list.length - 1];
  const maxTo = Number(last?.ToMinute ?? 0);

  if (value > maxTo) {
    return {
      usedMinutes: maxTo,
      factor: Number(last?.FactorNb ?? 0),
    };
  }

  return { usedMinutes: 0, factor: 0 };
};

export const calculateRulesMinutesForDay = (dayObj, iso, ruleRows, leaveOptions) => {
  const dayKey = getDayKeyFromIso(iso);
  const supposedTxt = String(dayObj?.rulestime?.[dayKey] ?? "—").trim();
  const supposed = getSupposedMinMaxFromRuleText(supposedTxt);
  const working = getWorkingMinMaxFromCellText(String(dayObj?.time || ""), leaveOptions);

  if (!working.hasWork) {
    return {
      totalMinutes: 0,
      totalHours: 0,
      breakdown: { EC: 0, EL: 0, LC: 0, LL: 0 },
      weighted: { EC: 0, EL: 0, LC: 0, LL: 0 },
    };
  }

  const ec =
    working.minIn != null && supposed.minIn != null && working.minIn < supposed.minIn
      ? supposed.minIn - working.minIn
      : 0;

  const lc =
    working.minIn != null && supposed.minIn != null && working.minIn > supposed.minIn
      ? working.minIn - supposed.minIn
      : 0;

  const el =
    working.maxOut != null && supposed.maxOut != null && working.maxOut < supposed.maxOut
      ? supposed.maxOut - working.maxOut
      : 0;

  const ll =
    working.maxOut != null && supposed.maxOut != null && working.maxOut > supposed.maxOut
      ? working.maxOut - supposed.maxOut
      : 0;

  const weightedEC = ec * findFactorForMinutes(ruleRows, RULE_METHOD_KEYS.EC, ec).factor;
  const weightedEL = el * findFactorForMinutes(ruleRows, RULE_METHOD_KEYS.EL, el).factor;
  const weightedLC = lc * findFactorForMinutes(ruleRows, RULE_METHOD_KEYS.LC, lc).factor;
  const weightedLL = ll * findFactorForMinutes(ruleRows, RULE_METHOD_KEYS.LL, ll).factor;

  const totalMinutes = weightedEC + weightedEL + weightedLC + weightedLL;

  return {
    totalMinutes,
    totalHours: totalMinutes / 60,
    breakdown: { EC: ec, EL: el, LC: lc, LL: ll },
    weighted: { EC: weightedEC, EL: weightedEL, LC: weightedLC, LL: weightedLL },
  };
};

export const splitLeaveTypes = (s) =>
  String(s ?? "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

export const leaveNameById = (leaveOptions, id) => {
  const found = (leaveOptions || []).find((x) => String(x.leaveid) === String(id));
  return found?.leavename || `Leave ${id}`;
};

export const timesToSegments = (times, mode, leaveid) => {
  const segs = [];
  // TWO times → full pair (IN ~ OUT)
  for (let i = 0; i + 1 < times.length; i += 2) {
    segs.push({ mode, leaveid, time_in: times[i], time_out: times[i + 1] });
  }

  // ONE time only → show as single punch (IN only, no OUT yet)
  if (times.length % 2 !== 0) {
    const lastTime = times[times.length - 1];
    segs.push({ mode, leaveid, time_in: lastTime, time_out: "" });
    // ← empty time_out means employee hasn't punched out yet
  }
  return segs;
};

export const buildRulesMap = (rulesTime) => {
  const arr = Array.isArray(rulesTime) ? rulesTime : [];
  const first = arr[0] && typeof arr[0] === "object" ? arr[0] : null;
  if (!first) return {};

  const fiscalKey = Object.keys(first)[0];
  const block = fiscalKey ? first[fiscalKey] : null;
  const filters = Array.isArray(block?.Filters) ? block.Filters : [];

  const map = {};
  filters.forEach((f) => {
    const day = String(f?.DayList || "").trim();
    const wt = String(f?.WorkTime || "").trim();
    if (day && wt) map[day] = wt;
  });

  return map;
};

// Parses weeklyRules from api/attendances into { Mon: "08:00 ~ 17:00", ... }
// Handles flat array [{ DayList, WorkTime }], nested buildRulesMap format, or plain object.
export const buildWeeklyRulesMap = (weeklyRules) => {
  if (!weeklyRules) return {};

  if (Array.isArray(weeklyRules)) {
    const flatMap = {};
    weeklyRules.forEach((r) => {
      const day = String(r?.DayList || r?.dayList || "").trim();
      const wt = String(r?.WorkTime || r?.workTime || "")
        .trim()
        .replace(/\s*\|\s*/g, " ~ ")
        .replace(/\s*~\s*/g, " ~ ");
      if (day && wt) flatMap[day] = wt;
    });
    if (Object.keys(flatMap).length > 0) return flatMap;
    return buildRulesMap(weeklyRules);
  }

  if (typeof weeklyRules === "object") return weeklyRules;

  return {};
};

export const apiDayToGridCell = (dayObj, leaveOptions) => {
  const site = dayObj?.site || null;          //from location_confirmations (machine/GPS)
  const schedule = dayObj?.schedule || null;  // from pschdly (manual edit)
  const leave = dayObj?.leave || null;        // from pleave (manual leave)

  let segments = [];
  
  //* adds leave segments — leave is now an ARRAY (one entry per leave
 //* type on this day)
 const leaveList = Array.isArray(leave) ? leave : (leave ? [leave] : []);
 
 leaveList.forEach((lv) => { 
  if (!lv?.timeSch) return ;
    const leaveTypes = splitLeaveTypes(lv.leaveTypes);
    const leaveTimes = extractTimes(lv.timeSch);
    const leaveid = leaveTypes[0] || "";
    segments = segments.concat(timesToSegments(leaveTimes, "LEAVE", leaveid));
  });

  if (schedule?.timeSch) {
    const schTimes = extractTimes(schedule.timeSch);

    const moduleList = String(schedule?.stampModules ?? "")
      .split("|")
      .map((v) => { const n = parseInt(v.trim(), 10); return isNaN(n) ? null : n; });

      const dateList = String(schedule?.stampDates ?? "")
        .split("|")
        .map((v) => v.trim() || null);

    // one segment per individual time point (not pairs)
    for (let i = 0; i < schTimes.length; i++) {
      segments.push({
        mode:          "WORK",
        leaveid:       "DAWAM",
        time_in:       schTimes[i],
        time_out:      "",
        stampModuleID: moduleList[i] ?? null,
        stampDateTime: dateList[i] ?? null, 
      });
    }
  }

  // adds site segments
  if (site?.timeIn) {
    const siteTimes = extractTimes(site.timeIn);
    segments = segments.concat(timesToSegments(siteTimes, "WORK", "DAWAM"));
  }

   //  ADD THIS BLOCK — sort work segments by time_in, same as EditTimeDialog 
  const toMin = (hhmm) => {
    const parts = String(hhmm || "").split(":");
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1] || "0", 10);
    return isNaN(h) || isNaN(m) ? Infinity : h * 60 + m;
  };

  const workSegs  = segments.filter((s) => s.mode === "WORK");
  const leaveSegs = segments.filter((s) => s.mode !== "WORK");

  // build time points keeping track of which segment they came from
  const timePoints = [];
  workSegs.forEach((s) => {
    if (s.time_in)  timePoints.push({ min: toMin(s.time_in),  stampModuleID: s.stampModuleID, stampDateTime: s.stampDateTime });
    if (s.time_out) timePoints.push({ min: toMin(s.time_out), stampModuleID: s.stampModuleID, stampDateTime: s.stampDateTime });
  });
  timePoints.sort((a, b) => a.min - b.min);

  const toHHMM = (m) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

  const sortedWorkSegs = [];
  for (let i = 0; i < timePoints.length; i += 2) {
    if (i + 1 < timePoints.length) {
      // full pair
      sortedWorkSegs.push({
        mode:          "WORK",
        leaveid:       "DAWAM",
        time_in:       toHHMM(timePoints[i].min),
        time_out:      toHHMM(timePoints[i + 1].min),
        stampModuleID: timePoints[i].stampModuleID ?? null,
      });
    } else {
      // single punch — no time_out yet
      sortedWorkSegs.push({
        mode:          "WORK",
        leaveid:       "DAWAM",
        time_in:       toHHMM(timePoints[i].min),
        time_out:      "",
        stampModuleID: timePoints[i].stampModuleID ?? null,
      });
    }
  }

  segments = [...sortedWorkSegs, ...leaveSegs];

  const sortedModules = timePoints
    .map(p => {
      const mod = p.stampModuleID;
      return mod != null ? mod : "";
    })
    .join(" | ");
  //  END OF ADDED BLOCK 25/5/26

   const sortedStampDates = timePoints                  
    .map(p => p.stampDateTime || "")                  
    .join(" | ");   

  // encodes ALL segments into one text
  const timeText = encodeSegmentsToCellText(segments, leaveOptions);
  const status = site?.status ?? null;
  const address = String(site?.addressIn ?? "").trim().replace(/\s*\((?:IN|OUT)\)\s*/gi, " ").trim();

 
  //* from building one label, to joining several
  const leaveLabel = leaveList
  .filter((lv) => lv?.timeSch)
  .map((lv) => { 
    const types = splitLeaveTypes(lv.leaveTypes);
    const name = types.length ? leaveNameById(leaveOptions, types[0]) : "Leave";
    return `${name}: ${lv.timeSch}`;
  })
  .join(", ");

   //const leaveTypes = splitLeaveTypes(leave?.leaveTypes);
  // const leaveLabel =
  //   leave?.timeSch && leaveTypes.length
  //     ? `${leaveNameById(leaveOptions, leaveTypes[0])}: ${leave.timeSch}`
  //     : leave?.timeSch
  //       ? `Leave: ${leave.timeSch}`
  //       : "";

  const sumtimesh =
    schedule?.sumtimesh ?? schedule?.sumTimesH ?? schedule?.SumTimesH ?? null;

  const dayRuleTime =
    dayObj?.rulestime ??
    dayObj?.ruleTime ??
    dayObj?.rulesTime ??
    dayObj?.schedule?.rulestime ??
    dayObj?.schedule?.rulesTime ??
    null;

  const rulesMap = buildRulesMap(dayRuleTime);

  return {
    time: timeText,  // time: all 3 combined 
    status,
    address,
    sumtimesh,
    rulestime: dayRuleTime,
    rulesMap,
    //stampModules: schedule?.stampModules || "",  // ← NEW: "1 | 3" 21/5
    stampModules: sortedModules || schedule?.stampModules || "", //25/5/26
    stampDates: sortedStampDates || schedule?.stampDates || "",
    _src: {
      site,
      schedule,
      leave,
      leaveLabel,
      scheduleText: schedule?.timeSch || "",
    },
    _segments: segments,
  };
};

export const cellMatchesLeaveFilter = (cellText, selectedLeaves, leaveOptions) => {
  if (!selectedLeaves?.length) return true;

  const selectedIds = new Set(selectedLeaves.map((x) => String(x.leaveid)));
  const selectedNames = new Set(
    selectedLeaves.map((x) => String(x.leavename || "").trim().toLowerCase()),
  );

  const segments = decodeCellTextToSegments(cellText, leaveOptions);

  return segments.some((seg) => {
    const mode = String(seg?.mode || "").toUpperCase();
    const leaveid = String(seg?.leaveid || "");

    if (mode === "WORK") {
      return selectedIds.has("DAWAM") || selectedNames.has("working");
    }

    return selectedIds.has(leaveid);
  });
};

export const cellMatchesTimingFilter = (cell, iso, leaveOptions, selectedFilter) => {
  if (!selectedFilter?.key) return true;

  const cellText = String(cell?.time || "").trim();
  const working = getWorkingMinMaxFromCellText(cellText, leaveOptions);

  if (selectedFilter.key === "NOT_ATTENDANT") {
    if (!working.hasWork) return true;
    return !isTimeCellComplete(cellText);
  }

  const dayKey = getDayKeyFromIso(iso);
  const ruleTxt = String(cell?.rulestime?.[dayKey] ?? "").trim();
  const supposed = getSupposedMinMaxFromRuleText(ruleTxt);

  if (!working.hasWork || !isTimeCellComplete(cellText)) return false;

  switch (selectedFilter.key) {
    case "LATE_LEAVING":
      return working.maxOut != null && supposed.maxOut != null && working.maxOut > supposed.maxOut;
    case "LATE_COMING":
      return working.minIn != null && supposed.minIn != null && working.minIn > supposed.minIn;
    case "EARLY_LEAVING":
      return working.maxOut != null && supposed.maxOut != null && working.maxOut < supposed.maxOut;
    case "EARLY_COMING":
      return working.minIn != null && supposed.minIn != null && working.minIn < supposed.minIn;
    default:
      return true;
  }
};

export const shouldShowDayCellByFilters = (
  cell,
  iso,
  selectedLeaves,
  leaveOptions,
  selectedTimingFilter,
) => {
  const leaveOk =
    !selectedLeaves?.length ||
    cellMatchesLeaveFilter(String(cell?.time || ""), selectedLeaves, leaveOptions);

  if (selectedTimingFilter?.key === "NOT_ATTENDANT") return leaveOk;

  const timingOk = cellMatchesTimingFilter(cell, iso, leaveOptions, selectedTimingFilter);
  return leaveOk && timingOk;
};

export const getTotalHours = (emp, daysList) => {
  let totalMinutes = 0;

  daysList.forEach((iso) => {
    const cell = emp.days[iso];
    if (!cell || !cell.time || isDashOnly(cell.time)) return;
    const times = extractTimes(cell.time);
    if (!times || times.length < 2) return;

    for (let i = 0; i + 1 < times.length; i += 2) {
      const tIn = times[i];
      const tOut = times[i + 1];
      if (!tIn || !tOut) continue;

      const dIn = dayjs(`2020-01-01T${tIn}:00`);
      const dOut = dayjs(`2020-01-01T${tOut}:00`);
      const diff = dOut.diff(dIn, "minute");

      if (diff > 0 && diff < 24 * 60) totalMinutes += diff;
    }
  });

  return totalMinutes / 60;
};

export const getCellHours = (cell) => {
  if (!cell || !cell.time || isDashOnly(cell.time)) return 0;
  const times = extractTimes(cell.time);
  // if (!times || times.length < 2) return 0;
  if (!times || times.length === 0) return 0;
  if (times.length === 1) return 0;  // single punch → 0 hours (no OUT yet)

  let minutes = 0;
  for (let i = 0; i + 1 < times.length; i += 2) {
    const tIn = times[i];
    const tOut = times[i + 1];
    if (!tIn || !tOut) continue;

    const dIn = dayjs(`2020-01-01T${tIn}:00`);
    const dOut = dayjs(`2020-01-01T${tOut}:00`);
    const diff = dOut.diff(dIn, "minute");

    if (diff > 0 && diff < 24 * 60) minutes += diff;
  }

  return minutes / 60;
};

export const normalizeSch = (v) =>
  String(v ?? "")
    .trim()
    .replace(/\s*\|\s*/g, " ~ ")
    .replace(/\s*~\s*/g, " ~ ");

export const extractFirstPair = (v) => {
  const t = extractTimes(v);
  if (!t || t.length < 2) return null;  //chnage from 2 to 1 
  return { in: t[0], out: t[1] };
};

export const scheduleMinutes = (schText) => {
  const sch = normalizeSch(schText);
  if (!sch || isDashOnly(sch)) return null;
  const p = extractFirstPair(sch);
  if (!p) return null;
  const m = minutesDiff(p.in, p.out);
  return m > 0 ? m : null;
};

export const formatSignedHours = (mins) => {
  if (mins == null) return "—";
  const h = mins / 60;
  const sign = mins > 0 ? "+" : mins < 0 ? "−" : "";
  return `${sign}${Math.abs(h).toFixed(2)} h`;
};

export function renderStatus(val) {
  const s = typeof val === "string" ? val.toLowerCase() : val;

  if (val === 1 || s === "posted") {
    return <Chip label="Posted" size="small" color="success" sx={{ fontWeight: 700 }} />;
  }
  if (val === 2 || s === "machine") {
    return <Chip label="Machine" size="small" color="info" sx={{ fontWeight: 700 }} />;
  }
  if (val === 3 || s === "site") {
    return <Chip label="Site" size="small" color="warning" sx={{ fontWeight: 700 }} />;
  }
  if (s === "weekend") {
    return <Chip label="Weekend" size="small" variant="outlined" sx={{ fontWeight: 700 }} />;
  }

  return <span style={{ opacity: 0.6 }}>—</span>;
}

//gps:red, manual:blus,machine :orange,leave:purple
export const renderCellTextStyled = (cellText, stampModules, stampDates, onGpsClick ,onStampClick) => {
      console.log("DEBUG renderCellTextStyled:", { stampModules, stampDates, hasOnStampClick: !!onStampClick });
      console.log("DEBUG typeof stampDates:", typeof stampDates, stampDates);
    const modules = String(stampModules || "")
        .split("|")
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n));

    const dates = String(stampDates || "")
      .split("|")
      .map(s => s.trim());    

    const getTimeColor = (timeIndex) => {
        const mod = modules[timeIndex] ?? 3;
        if (mod === 1) return { color: "#b71c1c", fontWeight: 900 };  // GPS → RED
        if (mod === 2) return { color: "#e65100", fontWeight: 900 };  // Machine → ORANGE
        if (mod === 4) return { color: "#2e7d32", fontWeight: 900 };  // Employee manual
        return { color: "#0d47a1", fontWeight: 900 };                 // HR → BLUE
    };

    const lines = String(cellText ?? "").split("\n");
    let timeIndex = 0;  // tracks each individual time across ALL lines

    return (
        <Stack spacing={0.35} sx={{ alignItems: "center" }}>
            {lines.map((raw, idx) => {
                const line = String(raw || "").trim();
                if (!line) return null;

                if (line === DIVIDER) {
                    return (
                        <Box key={`div-${idx}`} sx={{
                            width: "86%", height: "1px",
                            bgcolor: "rgba(0,0,0,.18)", my: 0.2,
                        }} />
                    );
                }

                const head = line.split(":")[0]?.trim() || "";
                const headLower = head.toLowerCase();
                const isWorking = headLower === "working" || headLower === "dawam";

                if (!isWorking) {
                // leave → always purple, no timeIndex increment
                const colonIdx = line.indexOf(":");
                const leaveTimes = colonIdx >= 0 ? line.slice(colonIdx + 1).trim() : "";
                return (
                    <Box key={`l-${idx}`} sx={{
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.1, px: 0.9, py: 0.35,
                        borderRadius: 1.8, fontSize: 12, fontWeight: 800,
                        lineHeight: 1.25,
                        bgcolor: "rgba(156,39,176,.12)",
                        color: "#4a148c",
                        border: "1px solid rgba(156,39,176,.28)",
                    }}>
                        <span>{leaveTimes ? head : line}</span>
                        {leaveTimes && (
                            <span style={{ whiteSpace: "nowrap" }}>{leaveTimes}</span>
                        )}
                    </Box>
                );
            }

                // extract times from this working line
                const timesInLine = extractTimes(line);
                const timeIn  = timesInLine[0] || "";
                const timeOut = timesInLine[1] || "";

                const colorIn  = getTimeColor(timeIndex);
                const colorOut = getTimeColor(timeIndex + 1);
                timeIndex += timesInLine.length;  // advance by number of times in this line

                // return (
                //     <Box key={`l-${idx}`} sx={{
                //         display: "inline-flex",
                //         alignItems: "center",
                //         gap: 0.3,
                //         px: 0.9, py: 0.35,
                //         borderRadius: 1.8,
                //         fontSize: 12,
                //         fontWeight: 800,      
                //         lineHeight: 1.2,
                //         bgcolor: "rgba(224,242,241,.45)",           //  neutral teal background
                //         border: "1px solid rgba(128,203,196,.4)",   //  soft teal border
                //     }}>
                //         {/* time_in with its own color */}
                //         <span style={{ ...colorIn, fontWeight: 800 }}>{timeIn}</span>

                //         {timeOut && (
                //             <>
                //                 <span style={{ opacity: 0.5, fontSize: 11, fontWeight: 800 }}>~</span>
                //                 {/* time_out with its own color */}
                //                 <span style={{ ...colorOut, fontWeight: 800 }}>{timeOut}</span>
                //             </>
                //         )}
                //     </Box>
                // );

                // 26/5/26 onclick the address will appear 
                const inIsGps  = (modules[timeIndex - timesInLine.length] ?? 3) === 1;
                const outIsGps = (modules[timeIndex - timesInLine.length + 1] ?? 3) === 1;

                const inIsEmployeeManual  = (modules[timeIndex - timesInLine.length] ?? 3) === 4;       
                const outIsEmployeeManual = (modules[timeIndex - timesInLine.length + 1] ?? 3) === 4;  
                const inStampDate  = dates[timeIndex - timesInLine.length] || "";                  
                const outStampDate = dates[timeIndex - timesInLine.length + 1] || "";

                 console.log("DEBUG segment:", { timeIn, timeOut, inIsEmployeeManual, outIsEmployeeManual, inStampDate, outStampDate }); 
                return (
                    <Box key={`l-${idx}`} sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.3,
                        px: 0.9, py: 0.35,
                        borderRadius: 1.8,
                        fontSize: 12,
                        fontWeight: 800,      
                        lineHeight: 1.2,
                        bgcolor: "rgba(224,242,241,.45)",
                        border: "1px solid rgba(128,203,196,.4)",
                    }}>
                        {/* time_in */}
                        <span
                            style={{
                                ...colorIn,
                                fontWeight: 800,
                                cursor: (inIsGps || inIsEmployeeManual) ? "pointer" : "default",
                                textDecoration: (inIsGps || inIsEmployeeManual) ? "underline dotted" : "none",
                            }}
                            onClick={(e) => {
                                // if (!inIsGps || !onGpsClick) return;
                                // e.stopPropagation();
                                // onGpsClick(e, timeIn);
                              if (inIsGps && onGpsClick) {              // ← changed (was: if (!inIsGps || !onGpsClick) return; onGpsClick(...))
                                    e.stopPropagation();
                                    onGpsClick(e, timeIn);
                                    return;
                                }
                                if (inIsEmployeeManual && onStampClick) {  // ← add
                                    e.stopPropagation();
                                    onStampClick(e, timeIn, inStampDate);
                                }  
                            }}
                        >
                            {timeIn}
                        </span>

                        {timeOut && (
                            <>
                                <span style={{ opacity: 0.5, fontSize: 11, fontWeight: 800 }}>~</span>
                                {/* time_out */}
                                <span
                                    style={{
                                        ...colorOut,
                                        fontWeight: 800,
                                        cursor: (outIsGps || outIsEmployeeManual) ? "pointer" : "default",
                                        textDecoration: (outIsGps || outIsEmployeeManual) ? "underline dotted" : "none",
                                    }}
                                    onClick={(e) => {
                                        // if (!outIsGps || !onGpsClick) return;
                                        // e.stopPropagation();
                                        // onGpsClick(e, timeOut);
                                      if (outIsGps && onGpsClick) {              // ← changed
                                            e.stopPropagation();
                                            onGpsClick(e, timeOut);
                                            return;
                                        }
                                        if (outIsEmployeeManual && onStampClick) {  // ← add
                                            e.stopPropagation();
                                            onStampClick(e, timeOut, outStampDate);
                                        }  
                                    }}
                                >
                                    {timeOut}
                                </span>
                            </>
                        )}
                    </Box>
                );
                            })}
                        </Stack>
                    );
                };

 
//mhmd dakd 21/5/26
// export const renderCellTextStyled = (cellText) => {
//   const lines = String(cellText ?? "").split("\n");

//   return (
//     <Stack spacing={0.35} sx={{ alignItems: "center" }}>
//       {lines.map((raw, idx) => {
//         const line = String(raw || "").trim();
//         if (!line) return null;

//         if (line === DIVIDER) {
//           return (
//             <Box
//               key={`div-${idx}`}
//               sx={{
//                 width: "86%",
//                 height: "1px",
//                 bgcolor: "rgba(0,0,0,.18)",
//                 my: 0.2,
//               }}
//             />
//           );
//         }

//         const head = line.split(":")[0]?.trim() || "";
//         const headLower = head.toLowerCase();
//         const isWorking =
//           headLower === "working" || headLower === "dawam" || head === "دوام";

//         const badgeSx = isWorking
//           ? {
//               bgcolor: "rgba(38,166,154,.14)",
//               color: "#004d40",
//               border: "1px solid rgba(38,166,154,.35)",
//             }
//           : {
//               bgcolor: "rgba(156,39,176,.12)",
//               color: "#4a148c",
//               border: "1px solid rgba(156,39,176,.28)",
//             };

//         return (
//           <Box
//             key={`l-${idx}`}
//             sx={{
//               display: "inline-flex",
//               alignItems: "center",
//               gap: 0.8,
//               px: 0.9,
//               py: 0.35,
//               borderRadius: 1.8,
//               fontSize: 12,
//               fontWeight: 800,
//               lineHeight: 1.2,
//               ...badgeSx,
//             }}
//           >
//             {line}
//           </Box>
//         );
//       })}
//     </Stack>
//   );
// };